import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { BodyLink, Message, Spinner, Stack } from '@kvdbil/components';
import { useTranslation } from '~/Locale';
import { useDispatch, useSelector } from 'react-redux';
import { isAxiosError } from '~/helpers/axiosUtils';
import { verifyMemberEmail } from '~/App/shared/actions/session';
import { resendEmailVerification } from '~/helpers/orchestration/verification';
import { idToken as idTokenSelector } from '~/App/shared/selectors/sessionSelector';

const MessageContent = styled(Stack)`
  row-gap: 0.5rem;
`;

const ResendContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StyledBodyLink = styled(BodyLink)`
  cursor: pointer;
`;

const EmailVerificationRequiredMessage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const idToken = useSelector(idTokenSelector);
  const [isResending, setIsResending] = useState(false);
  const [resendError, setResendError] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleResendEmail = useCallback(async () => {
    if (!idToken || isResending) {
      return;
    }

    setIsResending(true);
    setResendError(false);
    setResendSuccess(false);

    try {
      await resendEmailVerification({ idToken });
      setResendSuccess(true);
    } catch (error: unknown) {
      if (
        isAxiosError<{ message: string }>(error) &&
        error.response?.data?.message === 'MEMBER_ALREADY_VERIFIED'
      ) {
        dispatch(verifyMemberEmail());
      } else {
        setResendError(true);
      }
    } finally {
      setIsResending(false);
    }
  }, [dispatch, idToken, isResending]);

  return (
    <Stack rowGap={0.5}>
      <Message type="error" fullWidth withIcon>
        <MessageContent>
          <div>
            {t(
              'You must verify your email before you can enter/verify your phone number.'
            )}
          </div>
          {!resendSuccess && (
            <ResendContainer>
              <StyledBodyLink onClick={handleResendEmail}>
                {t('Resend verification email')}
              </StyledBodyLink>
              {isResending && (
                <Spinner size="smaller" color="neutral" speed="fast" />
              )}
            </ResendContainer>
          )}
          {resendSuccess && (
            <strong>{t('Check your inbox to verify your e-mail')}</strong>
          )}
        </MessageContent>
      </Message>
      {resendError && (
        <Message type="error" fullWidth>
          {t('The confirmation email could not be sent')}
        </Message>
      )}
    </Stack>
  );
};

export default EmailVerificationRequiredMessage;
